import React, { useMemo } from 'react';
import { Card, Panel, Table } from '@/components/Bicester';
import { ColumnConfig } from '@/components/Bicester/Table';
import { IconButton } from 'rsuite';
import { FormikProvider, useFormik } from 'formik';
import { defaultQueryInfo, marginQuerySchema } from './constants';
import { Form } from '@/components/formik-rsuite';
import Image from '@/components/Bicester/Image';
import useContentHeight from '@/hooks/useContentHeight';
import { Cell, DateTimeCell } from '@/components/Bicester/Table/Cells';
import { useRequest } from 'ahooks';
import useTable from '@/hooks/useTable';
import { fetchOptionResidueList } from '@/services/subscribe/margin';
import _ from 'lodash';
import { DEFAULT_TABLE_VALUE } from '@/constants/config';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import { optionPickerData } from '../Margin/constants';

const MarginQuery = () => {
  const {
    run: getList,
    data: { option_residue: tableData },
    loading: getListLoading,
  } = useRequest<any>(fetchOptionResidueList, {
    initialData: DEFAULT_TABLE_VALUE,
    defaultParams: defaultQueryInfo,
  });

  const { onChange, onSearch } = useTable(getList, {
    option_id: defaultQueryInfo.option_id,
    date: defaultQueryInfo.date,
  });

  const formik = useFormik({
    initialValues: defaultQueryInfo,
    validationSchema: marginQuerySchema,
    enableReinitialize: true,
    onSubmit(values: any, actions) {
      onSearch(values);
    },
  });

  const isParking = useMemo(() => !_.isArray(tableData) && !!tableData?.time, [tableData]);

  const columns: ColumnConfig[] = [
    {
      columnProps: { flexGrow: isParking ? 1 : 2 },
      header: `${isParking ? '日期' : '时间段'}`,
      cell: isParking ? <DateTimeCell dataKey="time" /> : <Cell dataKey="time" />,
    },
    {
      columnProps: { flexGrow: 1 },
      header: '组数',
      cell: <Cell dataKey="group_use" />,
    },
    ...(isParking
      ? []
      : [
          {
            columnProps: { flexGrow: 1 },
            header: '人数',
            cell: <Cell dataKey="number_sum_use" />,
          },
        ]),
    {
      columnProps: { flexGrow: 1 },
      header: '余量限制组数',
      cell: <Cell dataKey="option_set_count" />,
    },
    ...(isParking
      ? []
      : [
          {
            columnProps: { flexGrow: 1 },
            header: '现场可预约组数',
            cell: <Cell dataKey="scene_residue_count" />,
          },
        ]),
    {
      columnProps: { flexGrow: 1 },
      header: '系统可预约组数',
      cell: <Cell dataKey="system_residue_count" />,
    },
  ];

  return (
    <div>
      <Panel dividerHeight={10} leftShadow title="查询条件">
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="flex items-center w-full">
                <div className="flex items-center w-[50%]">
                  <Card.Picker
                    required
                    label="服务类型"
                    formKey="option_id"
                    pickerData={optionPickerData}
                    cleanable={false}
                    labelWeight={3}
                  />
                  <Card.DatePicker
                    required
                    label="日期"
                    formKey="date"
                    cleanable={false}
                    labelWeight={3}
                  />
                </div>
                <IconButton type="submit" className="inline-flex items-center w-[30px] ml-[10px]">
                  <Image src="/resources/images/match.svg" />
                </IconButton>
              </div>
            </Card>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title="余量查询结果"
        dividerHeight={10}
        leftShadow
        style={{
          height:
            'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 169px), auto)',
        }}
      >
        <Table
          style={{
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 169
            }px, ${
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          loading={getListLoading}
          onChange={onChange}
          data={_.isArray(tableData) ? tableData : tableData?.time ? [tableData] : []}
          columns={columns}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelHeader: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withTablePagination: true,
              }) - 169,
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
        </Table>
      </Panel>
    </div>
  );
};

export default MarginQuery;
